import React, { Component } from "react";

import _ from "lodash";
import { Column, ColumnFilterElementTemplateOptions } from "primereact/column";
import { dateBodyTemplate } from "../../utils/columnTemplates";
import { DataTableSortMeta } from "primereact/datatable";

import { TransactionReportDetails, AllTransactionRequestModel, PaymentStatus, Transaction, TransactionStatus } from "../../models/Transactions";
import { dateRangeFilterTemplate, statusFilterMatchModeOptions, stringFilterMatchModeOptions } from "../Paginator";
import { Dropdown } from "primereact/dropdown";
import { toastService } from "../../services/toastService";
import { SelectItem } from "../../models/SelectItem";
import { Calendar } from "primereact/calendar";
import {
    RenderStatusBadge,
    TransactionTypeFilterTemplate,
} from "../Operations/TransactionFilterTemplates";

import { transactionsReportApiService } from "../../services/api/operations/TransactionsReportApiService";
import ExtendedDataTable from "../../components/ExtendedDataTable";
import { SearchRequestBaseModel, SortOrderType } from "../../models/SearchRequestModel";
import { DataTableToolbar } from "../../components/ui";
import { utils } from "../../utils/utils";
import { currenciesApiService } from "../../services/api/merchants/CurrenciesApiService";
import { storageService } from "../../services/StorageService";
import TransactionDetailsDialog from "./TransactionDetails";

interface TransactionsState {
    isLoading: boolean;
    transactions: TransactionReportDetails[];
    first: number;
    rows: number;
    filters?: SearchRequestBaseModel;
    multiSortMeta: DataTableSortMeta[];
    isTransactionDetailVisible: boolean;
    selectedTransaction: TransactionReportDetails | null;
    currencies: SelectItem[];
    totalRecords: number;
}

interface TransactionProps {
    userId: number;
    userEmail: string | undefined;
    showExpander: boolean;
}

export default class UserTransactions extends Component<TransactionProps, TransactionsState> {
    private dataTable: ExtendedDataTable | null = null;

    constructor(props: TransactionProps | Readonly<TransactionProps>) {
        super(props);

        this.state = {
            isLoading: true,
            transactions: [],
            multiSortMeta: [],
            first: 0,
            rows: 10,
            isTransactionDetailVisible: false,
            selectedTransaction: null,
            currencies: [],
            totalRecords: 0,
        };
    }

    async componentDidMount() {
        await Promise.all([this.loadCurrencies(), this.loadTransactions()]);
        this.setState({ isLoading: false });
    }

    loadCurrencies = async () => {
        const currenciesResult = await currenciesApiService.getMerchantActiveCurrencies(storageService.getLoggedInUserMerchantId());
        if (!currenciesResult.success) {
            toastService.error(currenciesResult.message);
            this.setState({ isLoading: false });
        }
        const currenciesList: SelectItem[] = currenciesResult.data.map((currency) => ({
            label: currency.charCode,
            value: currency.charCode,
        }));

        this.setState({ currencies: currenciesList });
    };

    onSorting = async (filters: SearchRequestBaseModel = {} as SearchRequestBaseModel, transactions?: TransactionReportDetails[]) => {
        this.setState({ transactions: await utils.extendedDatatableSorting(filters, transactions ?? this.state.transactions) });
    };

    loadTransactions = async (filters: SearchRequestBaseModel = {} as SearchRequestBaseModel) => {
        const filterRequest: AllTransactionRequestModel = await utils.extractFiltersData(filters);

        if (this.props.userId) {
            this.setState({ isLoading: true });

            filterRequest.individualId = this.props.userId;
            filterRequest.dataBatch = filters.pageNumber ?? 1;
            filterRequest.dataBatchCount = filters.pageSize ?? 10;
            const transactionResult = await transactionsReportApiService.getAllTransactionsReportResponse(filterRequest);

            if (!transactionResult.success) {
                this.setState({ isLoading: false });
                toastService.error(transactionResult.message);
                return;
            }

            await this.onSorting(filters, transactionResult.data.transactions);
            this.setState({ isLoading: false, totalRecords: transactionResult.data.transactionCount });
        }
    };

    renderHeader = (hasActiveFilters: boolean) => <DataTableToolbar onClear={() => this.dataTable!.reset()} hasActiveFilters={hasActiveFilters} />;

    // actionBodyTemplate = (rowData: Transaction) => {
    //     return (
    //         <React.Fragment>
    //             <Button icon="pi pi-info" className="p-button-rounded" onClick={() => this.showTransactionDetails(rowData)} />
    //         </React.Fragment>
    //     );
    // };

    currencyFilterTemplate = (options: ColumnFilterElementTemplateOptions, columnName: string, placeHolder: string) => {
        return (
            <Dropdown
                value={options.value}
                options={this.state.currencies}
                filter
                onChange={(e) => options.filterCallback(e.value, options.index)}
                placeholder={`Select ${placeHolder}`}
                className="p-column-filter"
                showClear
            />
        );
    };

    dateFilterTemplate = (options: ColumnFilterElementTemplateOptions) => {
        return (
            <Calendar
                value={options.value}
                onChange={(e) => options.filterCallback(e.value, options.index)}
                dateFormat="dd/mm/yy"
                placeholder="mm/dd/yyyy"
                mask="99/99/9999"
            />
        );
    };

    showTransactionDetails = (rowData: TransactionReportDetails) => {
        this.setState({ isTransactionDetailVisible: true, selectedTransaction: rowData });
    };

    renderStatusBody = (rowData: TransactionReportDetails) => {

        return RenderStatusBadge(rowData.transactionStatus);
    };

    render() {
        return (
            <>
                {this.state.isTransactionDetailVisible && this.state.selectedTransaction != null && (
                    <TransactionDetailsDialog
                        isVisible={this.state.isTransactionDetailVisible}
                        onHide={() => this.setState({ isTransactionDetailVisible: false })}
                        onRollbackSuccess={() => this.loadTransactions()}
                        transaction={this.state.selectedTransaction}
                        editable={false}
                    />
                )}

                <div>
                    <ExtendedDataTable
                        header={this.renderHeader}
                        value={this.state.transactions}
                        loading={this.state.isLoading}
                        totalRecords={this.state.totalRecords}
                        onChange={this.loadTransactions}
                        ref={(ref) => (this.dataTable = ref)}
                        defaultSortOrder={SortOrderType.Descending}
                        defaultSortField="timeStamp"
                        onSorting={this.onSorting}
                        onRowClick={(e) => this.showTransactionDetails(e.data as TransactionReportDetails)}
                    >
                        <Column
                            field="transactionType"
                            header="Type"
                            {...({ alwaysVisible: true } as any)}
                            body={(rowData: TransactionReportDetails) => <p>{rowData.transactionType}</p>}
                            filterMatchModeOptions={stringFilterMatchModeOptions}
                            showFilterMatchModes={false}
                            filterField="trxDirection"
                            filterElement={TransactionTypeFilterTemplate}
                        ></Column>
                        <Column
                            field="accountNumber"
                            header="Wallet Id"
                            {...({ alwaysVisible: true } as any)}
                            body={(rowData: TransactionReportDetails) => <p>{rowData.accountNumber}</p>}
                            //filterElement={(options => this.transactionTypeFilterTemplate(options, 'walletId', 'Wallet Id'))}
                            showFilterMatchModes={false}
                            filterField="accountID"
                            filterPlaceholder="Search by Wallet ID"
                            filterMatchModeOptions={stringFilterMatchModeOptions}
                        />
                        <Column
                            field="timeStamp"
                            {...({ alwaysVisible: true } as any)}
                            header="Transaction Date"
                            body={dateBodyTemplate("timeStamp")}
                            dataType="date"
                            filterElement={dateRangeFilterTemplate}
                            filterField="timeStampTo"
                            filterMatchModeOptions={statusFilterMatchModeOptions}
                            showFilterMatchModes={false}
                        ></Column>
                        <Column
                            field="amount"
                            header="Amount"
                            showFilterMatchModes={false}
                            filterField="amountTo"
                            filterPlaceholder="Search by Amount"
                            filterMatchModeOptions={stringFilterMatchModeOptions}
                            body={(rowData: TransactionReportDetails) => <p>{utils.renderAmount(rowData.amount)}</p>} />
                        <Column
                            field="currency"
                            header="Currency"
                            body={(rowData: TransactionReportDetails) => <p>{rowData.currency}</p>}
                            showFilterMatchModes={false}
                            filterField="currency"
                            filterPlaceholder="Search by Currency"
                            filterMatchModeOptions={stringFilterMatchModeOptions}
                            filterElement={(options) => this.currencyFilterTemplate(options, "currency", "Currency")}
                        ></Column>
                        <Column
                            field="transactionId"
                            header="Reference Number"
                            body={(rowData: TransactionReportDetails) => <p>{rowData.transactionId}</p>}
                            showFilterMatchModes={false}
                            filterField="transactionID"
                            filterPlaceholder="Search by Transaction ID"
                            filterMatchModeOptions={stringFilterMatchModeOptions}
                        //filterElement={(options => this.transactionTypeFilterTemplate(options, 'id', 'Reference Number'))}
                        ></Column>
                        <Column
                            field="status"
                            header="Status"
                            body={this.renderStatusBody}
                        //filterElement={(options => this.transactionTypeFilterTemplate(options, 'status', 'Status'))}
                        ></Column>

                    </ExtendedDataTable>
                </div>
            </>
        );
    }
}
